import {
  ArchiveIcon,
  ArrowsClockwiseIcon,
  EyeIcon,
  GearIcon,
  PrinterIcon,
  TrayArrowUpIcon,
} from '@phosphor-icons/react';
import type { ReportStatus } from '@act/shared/data-transfer-objects';
import { FlexContainer } from '@act/storybook';
import { useNavigate } from 'react-router-dom';
import ReportIconActionButton from './ReportIconActionButton';

const REPORT_ICON_SIZE = 20;

export interface ReportBuilderHeaderActionsProps {
  reportId: string;
  status: ReportStatus;
  canRefreshData: boolean;
  refreshingData: boolean;
  archiving: boolean;
  unarchiving: boolean;
  onRefreshData: () => void;
  onArchiveToggle: () => void;
}

/**
 * Icon toolbar for report builder actions (view, print, settings, refresh, archive).
 * @param props - Report identity, action state flags, and click handlers.
 */
const ReportBuilderHeaderActions = ({
  reportId,
  status,
  canRefreshData,
  refreshingData,
  archiving,
  unarchiving,
  onRefreshData,
  onArchiveToggle,
}: ReportBuilderHeaderActionsProps) => {
  const navigate = useNavigate();
  const archiveBusy = archiving || unarchiving;
  const isArchived = status === 'archived';

  return (
    <FlexContainer
      direction="row"
      gap={0.5}
      alignItems="center"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportIconActionButton
        label="View"
        trackingName="reports-builder-view"
        onClick={() => navigate(`/${reportId}/view`)}
      >
        <EyeIcon size={REPORT_ICON_SIZE} />
      </ReportIconActionButton>
      <ReportIconActionButton
        label="Print / Save as PDF"
        trackingName="reports-builder-print"
        onClick={() => navigate(`/${reportId}/print`)}
      >
        <PrinterIcon size={REPORT_ICON_SIZE} />
      </ReportIconActionButton>
      <ReportIconActionButton
        label="Edit settings"
        trackingName="reports-builder-edit-settings"
        onClick={() => navigate(`/${reportId}/edit`)}
      >
        <GearIcon size={REPORT_ICON_SIZE} />
      </ReportIconActionButton>
      <ReportIconActionButton
        label="Refresh data"
        trackingName="reports-builder-refresh-data"
        disabled={!canRefreshData || refreshingData}
        onClick={onRefreshData}
      >
        <ArrowsClockwiseIcon size={REPORT_ICON_SIZE} />
      </ReportIconActionButton>
      <ReportIconActionButton
        label={isArchived ? 'Restore' : 'Archive'}
        trackingName="reports-builder-archive"
        disabled={archiveBusy}
        onClick={onArchiveToggle}
      >
        {isArchived ? (
          <TrayArrowUpIcon size={REPORT_ICON_SIZE} />
        ) : (
          <ArchiveIcon size={REPORT_ICON_SIZE} />
        )}
      </ReportIconActionButton>
    </FlexContainer>
  );
};

export default ReportBuilderHeaderActions;
